import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { UserSearchCombobox } from "../../../../components/ui/user-search-combobox";
import {
  FieldGroup,
  FieldGroupInstance,
  FieldDefinition,
  FieldType,
} from "../../../../models/audit/library";
import {
  useCreateFieldGroupInstance,
  useUpdateFieldGroupInstance,
} from "../../../../hooks/useLibraries";

interface AddLibraryItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldGroup: FieldGroup;
  editingInstance: FieldGroupInstance | null;
  onSuccess: () => void;
}

// Type for the wrapped field value format from the API
type WrappedFieldValue =
  | { text: string }
  | { userIds: string[] }
  | { datetime: string };

// Type for form field values (supports arrays for multi-select fields)
type FieldValue = string | string[] | number | boolean | null;

// Helper to unwrap field value from API format to form value
function unwrapFieldValue(
  wrappedValue: unknown,
  includeTime?: boolean,
): FieldValue {
  if (wrappedValue === null || wrappedValue === undefined) return null;
  if (typeof wrappedValue === "object" && wrappedValue !== null) {
    const obj = wrappedValue as Record<string, unknown>;
    // Check for wrapped format {fieldType: value}
    if ("text" in obj) return obj.text as string;
    if ("userIds" in obj) {
      // Return the full array for multi-select
      return obj.userIds as string[];
    }
    if ("datetime" in obj) {
      const datetime = obj.datetime as string;
      if (includeTime) {
        // For datetime-local input, format as YYYY-MM-DDTHH:MM
        const date = new Date(datetime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      // Extract date portion (YYYY-MM-DD) from ISO 8601 datetime for date input
      return datetime.split("T")[0];
    }
  }
  // Fallback for simple values (backward compatibility)
  if (
    typeof wrappedValue === "string" ||
    typeof wrappedValue === "number" ||
    typeof wrappedValue === "boolean"
  ) {
    return wrappedValue;
  }
  return null;
}

// Helper to wrap field value for API format
function wrapFieldValue(
  value: FieldValue,
  fieldType: FieldType,
  includeTime?: boolean,
): WrappedFieldValue | null {
  if (value === null || value === undefined) return null;

  switch (fieldType) {
    case "text": {
      if (value === "") return null;
      return { text: String(value) };
    }
    case "user": {
      // Handle array of user IDs
      const userIds = Array.isArray(value) ? value : [];
      if (userIds.length === 0) return null;
      return { userIds };
    }
    case "date": {
      if (value === "") return null;
      const stringValue = String(value);
      if (includeTime && stringValue.includes("T")) {
        // datetime-local gives local time - convert to UTC ISO string
        const localDate = new Date(stringValue);
        return { datetime: localDate.toISOString() };
      }
      // Date-only: use noon UTC to avoid date shifting across timezones
      return { datetime: `${stringValue}T12:00:00Z` };
    }
    default:
      if (value === "") return null;
      return { text: String(value) };
  }
}

export function AddLibraryItemModal({
  open,
  onOpenChange,
  fieldGroup,
  editingInstance,
  onSuccess,
}: AddLibraryItemModalProps) {
  const [slugSuffix, setSlugSuffix] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Local state stores unwrapped values for form handling (supports arrays for multi-select)
  const [fieldValues, setFieldValues] = useState<Record<string, FieldValue>>(
    {},
  );

  const { createInstance, loading: loadingCreate } =
    useCreateFieldGroupInstance();
  const { updateInstance, loading: loadingUpdate } =
    useUpdateFieldGroupInstance();

  const isEditing = !!editingInstance;
  const loading = loadingCreate || loadingUpdate;

  // Reset form when modal opens/closes or editing instance changes
  useEffect(() => {
    if (open) {
      if (editingInstance) {
        setSlugSuffix(editingInstance.slugSuffix);
        setName(editingInstance.name);
        setDescription(editingInstance.description || "");
        // Extract field values from the values array
        const unwrapped: Record<string, FieldValue> = {};
        if (editingInstance.values) {
          for (const valueEntry of editingInstance.values) {
            const fieldConfig = (valueEntry.fieldDefinition.fieldConfig ??
              {}) as Record<string, unknown>;
            const includeTime = fieldConfig.include_time === true;
            unwrapped[valueEntry.fieldDefinition.id] = unwrapFieldValue(
              valueEntry.value,
              includeTime,
            );
          }
        }
        setFieldValues(unwrapped);
      } else {
        // Clear slug suffix for new items (will be auto-generated if left empty)
        setSlugSuffix("");
        setName("");
        setDescription("");
        setFieldValues({});
      }
    }
  }, [open, editingInstance]);

  // Create a map of field definitions by ID for easy lookup
  const fieldDefMap = useMemo(() => {
    const map = new Map<string, FieldDefinition>();
    for (const def of fieldGroup.fieldDefinitions || []) {
      map.set(def.id, def);
    }
    return map;
  }, [fieldGroup.fieldDefinitions]);

  const handleFieldValueChange = (fieldId: string, value: FieldValue) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const renderFieldInput = (field: FieldDefinition) => {
    const value = fieldValues[field.id];
    const fieldConfig = (field.fieldConfig ?? {}) as Record<string, unknown>;

    switch (field.fieldType) {
      case "text":
        return (
          <Input
            value={typeof value === "string" ? value : ""}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}`}
            maxLength={
              typeof fieldConfig.max_length === "number"
                ? fieldConfig.max_length
                : 1000
            }
          />
        );
      case "user": {
        const allowMultiple = fieldConfig.allow_multiple === true;
        return (
          <UserSearchCombobox
            value={Array.isArray(value) ? value : []}
            onChange={(v) => handleFieldValueChange(field.id, v)}
            placeholder={`Select ${field.name.toLowerCase()}`}
            allowMultiple={allowMultiple}
            modal
          />
        );
      }
      case "date": {
        const includeTime = fieldConfig.include_time === true;
        return (
          <Input
            type={includeTime ? "datetime-local" : "date"}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => handleFieldValueChange(field.id, e.target.value)}
          />
        );
      }
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    // Wrap field values for API format: {fieldDefId: {fieldType: value}}
    const wrappedFieldValues: Record<string, WrappedFieldValue> = {};
    for (const [fieldId, value] of Object.entries(fieldValues)) {
      const fieldDef = fieldDefMap.get(fieldId);
      if (fieldDef && value !== null && value !== undefined && value !== "") {
        const fieldConfig = (fieldDef.fieldConfig ?? {}) as Record<
          string,
          unknown
        >;
        const includeTime = fieldConfig.include_time === true;
        const wrapped = wrapFieldValue(value, fieldDef.fieldType, includeTime);
        if (wrapped) {
          wrappedFieldValues[fieldId] = wrapped;
        }
      }
    }

    let success = false;
    if (isEditing && editingInstance) {
      const result = await updateInstance(editingInstance.id, {
        slugSuffix: slugSuffix.trim() || undefined,
        name: name.trim(),
        description: description.trim() || null,
        fieldValues:
          Object.keys(wrappedFieldValues).length > 0
            ? wrappedFieldValues
            : null,
      });
      success = !!result;
    } else {
      const result = await createInstance({
        groupId: fieldGroup.id,
        slugSuffix: slugSuffix.trim() || undefined, // Optional - auto-generated if empty
        name: name.trim(),
        description: description.trim() || null,
        fieldValues:
          Object.keys(wrappedFieldValues).length > 0
            ? wrappedFieldValues
            : null,
      });
      success = !!result;
    }

    if (success) {
      onSuccess();
    }
  };

  const fieldDefinitions = (fieldGroup.fieldDefinitions || []).sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Item" : "Add New Item"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Update the details for this ${fieldGroup.name.toLowerCase()} item.`
              : `Add a new item to the ${fieldGroup.name} library.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="slugSuffix">ID Suffix</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-stone-500">
                {fieldGroup.slugPrefix}-
              </span>
              <Input
                id="slugSuffix"
                value={slugSuffix}
                onChange={(e) => setSlugSuffix(e.target.value)}
                placeholder="001"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-stone-500">
              Leave empty to auto-generate the next sequential ID
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>

          {fieldDefinitions.map((field) => (
            <div key={field.id} className="space-y-2">
              <Label htmlFor={field.id}>
                {field.name}
                {field.isRequired && " *"}
              </Label>
              {renderFieldInput(field)}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || loading}
            loading={loading}
          >
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
